import { Browser } from '@capacitor/browser';
import { appStorage, runtimeConfig } from './runtime';

export type StripeCheckoutReturnOutcome = 'success' | 'cancel';

type PendingStripeCheckout = {
  paymentId: string;
  startedAtUtc: string;
};

type BrowserListenerHandle = {
  remove: () => Promise<void>;
};

const pendingStripeCheckoutStorageKey = 'stripe-checkout.pending';
const pendingStripeCheckoutMaxAgeMs = 24 * 60 * 60 * 1000;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export const persistPendingStripeCheckout = (paymentId: string): void => {
  const pending: PendingStripeCheckout = {
    paymentId,
    startedAtUtc: new Date().toISOString(),
  };

  appStorage.local.setItem(
    pendingStripeCheckoutStorageKey,
    JSON.stringify(pending),
  );
};

export const getPendingStripeCheckoutPaymentId = (): string | null => {
  const serialized = appStorage.local.getItem(pendingStripeCheckoutStorageKey);

  if (!serialized) {
    return null;
  }

  try {
    const pending = JSON.parse(serialized) as Partial<PendingStripeCheckout>;
    const startedAtUtc = Date.parse(pending.startedAtUtc ?? '');

    if (
      !isNonEmptyString(pending.paymentId) ||
      !Number.isFinite(startedAtUtc) ||
      Date.now() - startedAtUtc > pendingStripeCheckoutMaxAgeMs
    ) {
      appStorage.local.removeItem(pendingStripeCheckoutStorageKey);
      return null;
    }

    return pending.paymentId;
  } catch {
    appStorage.local.removeItem(pendingStripeCheckoutStorageKey);
    return null;
  }
};

export const clearPendingStripeCheckout = (paymentId?: string): void => {
  if (
    paymentId &&
    getPendingStripeCheckoutPaymentId() !== paymentId
  ) {
    return;
  }

  appStorage.local.removeItem(pendingStripeCheckoutStorageKey);
};

export const isStripeCheckoutReturnPath = (pathname: string): boolean =>
  pathname === '/checkout/stripe/success' ||
  pathname === '/checkout/stripe/cancel';

export const buildNativeStripeCheckoutReturnUrl = (
  outcome: StripeCheckoutReturnOutcome,
  search: string,
): string => {
  const query = new URLSearchParams(search);
  query.delete('return_target');
  const serializedQuery = query.toString();

  return `${runtimeConfig.nativeDeepLinkScheme}://checkout/stripe/${outcome}${
    serializedQuery ? `?${serializedQuery}` : ''
  }`;
};

export const openStripeHostedCheckout = async (
  url: string,
  onBrowserFinished?: () => void,
): Promise<BrowserListenerHandle | undefined> => {
  if (!runtimeConfig.isNativePlatform) {
    window.location.assign(url);
    return undefined;
  }

  let browserFinished = false;
  let listener: BrowserListenerHandle | undefined;

  if (onBrowserFinished) {
    listener = await Browser.addListener('browserFinished', () => {
      browserFinished = true;
      onBrowserFinished();
      void listener?.remove();
    });
  }

  try {
    await Browser.open({ url });
    return browserFinished ? undefined : listener;
  } catch (error) {
    await listener?.remove();
    throw error;
  }
};

export const closeStripeHostedCheckout = async (): Promise<void> => {
  if (!runtimeConfig.isNativePlatform) {
    return;
  }

  try {
    await Browser.close();
  } catch {
    // The browser may already be closed or the platform may have resumed via an external app.
  }
};
